import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { dayKey } from '../ui/format';
import { isBusy, type AgendaItem, type WindowItem } from './api';

/**
 * The day and week grids, and the three gestures that make a calendar a calendar.
 *
 * **Click a slot** to open a new event at that hour. **Drag across slots** to say how long it runs.
 * **Drag a block** to move it, or **drag its bottom edge** to change how long it is. Everything a
 * person does to a meeting on paper, they should be able to do here with one hand.
 *
 * The arithmetic is deliberately in one place. A grid is a linear map from a Y coordinate to a
 * minute of the day, and every gesture is that map applied twice — once where the pointer went down
 * and once where it is now. Scattering that across three handlers is how a drag ends up landing a
 * meeting fifteen minutes from where somebody let go.
 *
 * Pointer events rather than mouse events, so a stylus and a touch screen work without a second
 * implementation. A drag captures the pointer, which means the gesture survives the cursor leaving
 * the grid — the ordinary case when somebody drags a meeting to the bottom of the day.
 */

/** Minutes a drag snaps to. Fifteen is what people mean when they drag "to half past". */
const SNAP_MINUTES = 15;

/** How far a pointer must travel before a click becomes a drag. */
const DRAG_THRESHOLD_PX = 4;

/** Height of one hour, in pixels. Matches `--calendar-hour-height` in the stylesheet. */
const HOUR_HEIGHT = 48;

const MINUTES_PER_DAY = 24 * 60;

export interface GridEvent {
  item: WindowItem;
  /** Which column it belongs to: the index of its day within the grid. */
  dayIndex: number;
  /** Minutes from midnight. Clipped to the day, so a meeting spanning midnight draws in both. */
  startMinute: number;
  endMinute: number;
  /** How the block is laid out when meetings overlap. */
  lane: number;
  lanes: number;
}

export interface DraftRange {
  dayIndex: number;
  startMinute: number;
  endMinute: number;
}

export interface TimeGridProps {
  /** The days this grid draws, midnight-local for each. */
  days: Date[];
  items: WindowItem[];
  /**
   * Tasks and sprints falling in these days.
   *
   * Drawn in the all-day band rather than on the hour grid, because that is what they are: a task
   * has a due *date* and no hour, so there is no row on a clock where it belongs. The month view
   * showed them and the week view did not, which made a task look like it had vanished.
   */
  work?: readonly AgendaItem[];
  /** The colour to draw an item in, decided by the caller from its calendar. */
  colorOf: (item: WindowItem) => string;
  onOpen: (item: WindowItem) => void;
  /** Somebody picked an empty range: open a new event over it. */
  onCreate: (range: { startsAt: Date; endsAt: Date }) => void;
  /** Somebody moved or resized a block. */
  onMove: (item: WindowItem, range: { startsAt: Date; endsAt: Date }) => void;
  /** Where the grid scrolls to on first paint. Minutes from midnight. */
  scrollToMinute?: number;
}

type Gesture =
  | { kind: 'CREATE'; dayIndex: number; anchorMinute: number; currentMinute: number }
  | {
      kind: 'MOVE';
      item: WindowItem;
      dayIndex: number;
      grabOffsetMinutes: number;
      durationMinutes: number;
      currentMinute: number;
      currentDayIndex: number;
      moved: boolean;
    }
  | {
      kind: 'RESIZE';
      item: WindowItem;
      dayIndex: number;
      startMinute: number;
      currentMinute: number;
      moved: boolean;
    };

export function TimeGrid({
  days,
  items,
  work = [],
  colorOf,
  onOpen,
  onCreate,
  onMove,
  scrollToMinute = 7 * 60,
}: TimeGridProps): React.JSX.Element {
  const body = useRef<HTMLDivElement>(null);
  const columns = useRef<HTMLDivElement>(null);
  const [gesture, setGesture] = useState<Gesture | null>(null);

  // Opens on the morning rather than at midnight: eight empty hours is not where anybody works.
  useEffect(() => {
    if (body.current) body.current.scrollTop = (scrollToMinute / 60) * HOUR_HEIGHT;
  }, [scrollToMinute]);

  const laidOut = useMemo(() => layout(items, days), [items, days]);

  const allDay = useMemo(
    () =>
      laidOut.filter(
        (event) => (isBusy(event.item) ? event.item.allDay : event.item.allDay) === true,
      ),
    [laidOut],
  );

  const timed = useMemo(() => laidOut.filter((event) => !event.item.allDay), [laidOut]);

  /*
   * Tasks and sprints, per column.
   *
   * Matched on the local calendar day rather than by comparing instants: a task due on the 24th is
   * on the 24th in the reader's timezone, and an ISO string that lands at 02:00 UTC would otherwise
   * draw itself in the previous column.
   */
  const workByColumn = useMemo(() => {
    const columnsByKey = new Map(days.map((day, index) => [dayKey(day), index]));
    const out = new Map<number, AgendaItem[]>();

    for (const item of work) {
      const index = columnsByKey.get(dayKey(new Date(item.startsAt)));

      if (index === undefined) continue;

      out.set(index, [...(out.get(index) ?? []), item]);
    }

    return out;
  }, [work, days]);

  /** Where a pointer is, as a day column and a minute of that day. */
  const locate = useCallback(
    (clientX: number, clientY: number): { dayIndex: number; minute: number } | null => {
      const grid = columns.current;
      if (!grid) return null;

      const box = grid.getBoundingClientRect();
      const columnWidth = box.width / days.length;

      const dayIndex = Math.min(
        days.length - 1,
        Math.max(0, Math.floor((clientX - box.left) / columnWidth)),
      );

      const minute = ((clientY - box.top) / HOUR_HEIGHT) * 60;

      return { dayIndex, minute: clamp(minute, 0, MINUTES_PER_DAY) };
    },
    [days.length],
  );

  const instantOf = useCallback(
    (dayIndex: number, minute: number): Date => {
      const day = days[clamp(dayIndex, 0, days.length - 1)]!;

      return new Date(day.getTime() + snap(minute) * 60_000);
    },
    [days],
  );

  const onBackgroundDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    // Only the primary button starts a gesture; a right-click belongs to the browser.
    if (event.button !== 0) return;

    const at = locate(event.clientX, event.clientY);
    if (!at) return;

    event.currentTarget.setPointerCapture(event.pointerId);

    setGesture({
      kind: 'CREATE',
      dayIndex: at.dayIndex,
      anchorMinute: at.minute,
      currentMinute: at.minute,
    });
  };

  const onBlockDown = (
    event: React.PointerEvent<HTMLButtonElement>,
    block: GridEvent,
    handle: 'BODY' | 'BOTTOM',
  ): void => {
    if (event.button !== 0) return;

    const target = isBusy(block.item) ? null : block.item;

    // A block somebody may not edit is still clickable; it simply does not drag.
    if (!target?.canEdit) return;

    const at = locate(event.clientX, event.clientY);
    if (!at) return;

    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);

    setGesture(
      handle === 'BOTTOM'
        ? {
            kind: 'RESIZE',
            item: block.item,
            dayIndex: block.dayIndex,
            startMinute: block.startMinute,
            currentMinute: at.minute,
            moved: false,
          }
        : {
            kind: 'MOVE',
            item: block.item,
            dayIndex: block.dayIndex,
            grabOffsetMinutes: at.minute - block.startMinute,
            durationMinutes: block.endMinute - block.startMinute,
            currentMinute: at.minute,
            currentDayIndex: at.dayIndex,
            moved: false,
          },
    );
  };

  const onPointerMove = (event: React.PointerEvent): void => {
    if (!gesture) return;

    const at = locate(event.clientX, event.clientY);
    if (!at) return;

    setGesture((current) => {
      if (!current) return current;

      if (current.kind === 'CREATE') return { ...current, currentMinute: at.minute };

      // A gesture is a drag only once the pointer has actually travelled; otherwise it is a click,
      // and a click on a block opens it.
      const moved =
        current.moved ||
        Math.abs(at.minute - current.currentMinute) * (HOUR_HEIGHT / 60) > DRAG_THRESHOLD_PX ||
        (current.kind === 'MOVE' && at.dayIndex !== current.currentDayIndex);

      return current.kind === 'MOVE'
        ? { ...current, currentMinute: at.minute, currentDayIndex: at.dayIndex, moved }
        : { ...current, currentMinute: at.minute, moved };
    });
  };

  const onPointerUp = (): void => {
    if (!gesture) return;

    setGesture(null);

    if (gesture.kind === 'CREATE') {
      const from = Math.min(gesture.anchorMinute, gesture.currentMinute);
      const to = Math.max(gesture.anchorMinute, gesture.currentMinute);

      // A click rather than a drag: an hour from the slot, which is what somebody means by
      // clicking on "14h".
      const startMinute = snap(from);
      const endMinute = to - from < SNAP_MINUTES ? startMinute + 60 : snap(to);

      onCreate({
        startsAt: instantOf(gesture.dayIndex, startMinute),
        endsAt: instantOf(gesture.dayIndex, Math.max(endMinute, startMinute + SNAP_MINUTES)),
      });

      return;
    }

    if (!gesture.moved) {
      onOpen(gesture.item);

      return;
    }

    if (gesture.kind === 'MOVE') {
      const startMinute = snap(gesture.currentMinute - gesture.grabOffsetMinutes);

      onMove(gesture.item, {
        startsAt: instantOf(gesture.currentDayIndex, startMinute),
        endsAt: instantOf(gesture.currentDayIndex, startMinute + gesture.durationMinutes),
      });

      return;
    }

    const endMinute = Math.max(snap(gesture.currentMinute), gesture.startMinute + SNAP_MINUTES);

    onMove(gesture.item, {
      startsAt: instantOf(gesture.dayIndex, gesture.startMinute),
      endsAt: instantOf(gesture.dayIndex, endMinute),
    });
  };

  const draft = draftOf(gesture);

  return (
    <div className="timegrid">
      <div className="timegrid__head">
        <div className="timegrid__gutter" aria-hidden="true" />
        {days.map((day) => (
          <div
            key={day.toISOString()}
            className={`timegrid__day${isToday(day) ? ' is-today' : ''}`}
          >
            <span className="timegrid__weekday">{weekdayLabel(day)}</span>
            <span className="timegrid__date">{day.getDate()}</span>
          </div>
        ))}
      </div>

      {(allDay.length > 0 || workByColumn.size > 0) && (
        <div className="timegrid__allday">
          <div className="timegrid__gutter small muted">{t('calendar.allDay')}</div>
          {days.map((day, index) => (
            <div key={day.toISOString()} className="timegrid__alldaycell">
              {allDay
                .filter((event) => event.dayIndex === index)
                .map((event) => (
                  <button
                    key={`${event.item.id}-${event.startMinute}`}
                    type="button"
                    className="timegrid__chip"
                    style={{ borderInlineStartColor: colorOf(event.item) }}
                    onClick={() => onOpen(event.item)}
                  >
                    {label(event.item)}
                  </button>
                ))}

              {/* A link, not a button: a task lives on its own screen and this is the way there. */}
              {(workByColumn.get(index) ?? []).map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  className={`timegrid__chip timegrid__chip--${item.kind.toLowerCase()}`}
                  title={t(`calendar.kind.${item.kind}`)}
                >
                  {item.detail?.key ? `${item.detail.key} · ` : ''}
                  {item.kind === 'SPRINT_START'
                    ? t('calendar.sprintStart').replace('{name}', item.title)
                    : item.kind === 'SPRINT_END'
                      ? t('calendar.sprintEnd').replace('{name}', item.title)
                      : item.title}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="timegrid__body" ref={body}>
        <div className="timegrid__hours" aria-hidden="true">
          {Array.from({ length: 24 }, (_, hour) => (
            <div key={hour} className="timegrid__hour">
              <span>{hour === 0 ? '' : `${String(hour).padStart(2, '0')}:00`}</span>
            </div>
          ))}
        </div>

        <div
          className="timegrid__columns"
          ref={columns}
          style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}
          onPointerDown={onBackgroundDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {days.map((day, index) => (
            <div key={day.toISOString()} className="timegrid__column">
              {Array.from({ length: 24 }, (_, hour) => (
                <div key={hour} className="timegrid__slot" />
              ))}

              {isToday(day) && <Now />}

              {timed
                .filter((event) => event.dayIndex === index)
                .map((event) => (
                  <Block
                    key={`${event.item.id}-${event.startMinute}`}
                    event={event}
                    color={colorOf(event.item)}
                    dragging={gesture !== null && 'item' in gesture && gesture.item === event.item}
                    onDown={onBlockDown}
                  />
                ))}

              {draft && draft.dayIndex === index && (
                <div
                  className="timegrid__draft"
                  style={{
                    top: `${(draft.startMinute / 60) * HOUR_HEIGHT}px`,
                    height: `${Math.max(((draft.endMinute - draft.startMinute) / 60) * HOUR_HEIGHT, 12)}px`,
                  }}
                >
                  <span className="small">{`${clock(draft.startMinute)} – ${clock(draft.endMinute)}`}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Block({
  event,
  color,
  dragging,
  onDown,
}: {
  event: GridEvent;
  color: string;
  dragging: boolean;
  onDown: (
    pointerEvent: React.PointerEvent<HTMLButtonElement>,
    block: GridEvent,
    handle: 'BODY' | 'BOTTOM',
  ) => void;
}): React.JSX.Element {
  const busy = isBusy(event.item);
  const height = Math.max(((event.endMinute - event.startMinute) / 60) * HOUR_HEIGHT, 14);

  return (
    <button
      type="button"
      className={`timegrid__block${busy ? ' timegrid__block--busy' : ''}${
        dragging ? ' is-dragging' : ''
      }`}
      style={{
        top: `${(event.startMinute / 60) * HOUR_HEIGHT}px`,
        height: `${height}px`,
        insetInlineStart: `${(event.lane / event.lanes) * 100}%`,
        width: `${100 / event.lanes}%`,
        ...(busy ? {} : { background: tint(color), borderInlineStartColor: color }),
      }}
      onPointerDown={(pointerEvent) => onDown(pointerEvent, event, 'BODY')}
    >
      <span className="timegrid__blocktime">{clock(event.startMinute)}</span>
      <span className="timegrid__blocktitle">{label(event.item)}</span>

      {/* The bottom edge is the resize handle; it only exists on something editable. */}
      {!isBusy(event.item) && event.item.canEdit && (
        <span
          className="timegrid__handle"
          onPointerDown={(pointerEvent) => {
            pointerEvent.stopPropagation();
            onDown(
              pointerEvent as unknown as React.PointerEvent<HTMLButtonElement>,
              event,
              'BOTTOM',
            );
          }}
        />
      )}
    </button>
  );
}

/** The line across today, so "now" is somewhere rather than a thing to work out. */
function Now(): React.JSX.Element {
  const [minute, setMinute] = useState(() => minuteOfDay(new Date()));

  useEffect(() => {
    const timer = setInterval(() => setMinute(minuteOfDay(new Date())), 60_000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className="timegrid__now"
      style={{ top: `${(minute / 60) * HOUR_HEIGHT}px` }}
      aria-hidden="true"
    />
  );
}

// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function snap(minute: number): number {
  return clamp(Math.round(minute / SNAP_MINUTES) * SNAP_MINUTES, 0, MINUTES_PER_DAY);
}

function minuteOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

function clock(minute: number): string {
  const hour = Math.floor(minute / 60) % 24;
  const rest = Math.round(minute % 60);

  return `${String(hour).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

function isToday(day: Date): boolean {
  const now = new Date();

  return (
    day.getFullYear() === now.getFullYear() &&
    day.getMonth() === now.getMonth() &&
    day.getDate() === now.getDate()
  );
}

function weekdayLabel(day: Date): string {
  return t(`calendar.weekday.${day.getDay()}`);
}

function label(item: WindowItem): string {
  return isBusy(item) ? t('calendar.busy') : item.title;
}

/** A pale wash of the event's own colour, so the bar reads and the text stays legible. */
function tint(color: string): string {
  return `color-mix(in srgb, ${color} 18%, transparent)`;
}

function draftOf(gesture: Gesture | null): DraftRange | null {
  if (!gesture) return null;

  if (gesture.kind === 'CREATE') {
    const from = snap(Math.min(gesture.anchorMinute, gesture.currentMinute));
    const to = snap(Math.max(gesture.anchorMinute, gesture.currentMinute));

    return { dayIndex: gesture.dayIndex, startMinute: from, endMinute: Math.max(to, from + 15) };
  }

  if (!gesture.moved) return null;

  if (gesture.kind === 'MOVE') {
    const start = snap(gesture.currentMinute - gesture.grabOffsetMinutes);

    return {
      dayIndex: gesture.currentDayIndex,
      startMinute: start,
      endMinute: start + gesture.durationMinutes,
    };
  }

  return {
    dayIndex: gesture.dayIndex,
    startMinute: gesture.startMinute,
    endMinute: Math.max(snap(gesture.currentMinute), gesture.startMinute + SNAP_MINUTES),
  };
}

/**
 * Places every item in a column, clipped to the day, and works out how overlaps share the width.
 *
 * The lane algorithm is the simple one and the right one for a week: walk the events in order,
 * putting each in the first lane whose last event has finished. Two meetings at ten o'clock get
 * half the column each; three get a third. A calendar that stacks them invisibly is a calendar that
 * hides the clash it exists to reveal.
 */
export function layout(items: readonly WindowItem[], days: readonly Date[]): GridEvent[] {
  const out: GridEvent[] = [];

  for (const [dayIndex, day] of days.entries()) {
    const dayStart = day.getTime();
    const dayEnd = dayStart + MINUTES_PER_DAY * 60_000;

    const onThisDay = items
      .filter((item) => {
        const start = Date.parse(item.startsAt);
        const end = Date.parse(item.endsAt);

        return start < dayEnd && end > dayStart;
      })
      .map((item) => ({
        item,
        startMinute: clamp((Date.parse(item.startsAt) - dayStart) / 60_000, 0, MINUTES_PER_DAY),
        endMinute: clamp((Date.parse(item.endsAt) - dayStart) / 60_000, 0, MINUTES_PER_DAY),
      }))
      .sort((a, b) => a.startMinute - b.startMinute || b.endMinute - a.endMinute);

    // Each cluster of overlapping events shares its own width; a gap starts a new cluster, so one
    // clash in the morning does not narrow the whole afternoon.
    let cluster: typeof onThisDay = [];
    let clusterEnd = -1;

    const flush = (): void => {
      if (cluster.length === 0) return;

      const laneEnds: number[] = [];
      const placed = cluster.map((entry) => {
        let lane = laneEnds.findIndex((end) => end <= entry.startMinute);

        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(entry.endMinute);
        } else {
          laneEnds[lane] = entry.endMinute;
        }

        return { ...entry, lane };
      });

      for (const entry of placed) {
        out.push({ ...entry, dayIndex, lanes: laneEnds.length });
      }

      cluster = [];
      clusterEnd = -1;
    };

    for (const entry of onThisDay) {
      if (entry.startMinute >= clusterEnd && cluster.length > 0) flush();

      cluster.push(entry);
      clusterEnd = Math.max(clusterEnd, entry.endMinute);
    }

    flush();
  }

  return out;
}
